import React, { type FC } from 'react'
import { Box, MenuItem as MuiMenuItem, type MenuItemProps as MuiMenuItemProps, type SxProps, type Theme } from '@mui/material'

import { CheckIcon } from '../Branding'

/**
 * The MUI MenuItem with `dense`, `disableGutters`, and `divider` taken out.
 *
 * Spacing and appearance are theme-managed, so these props do nothing.
 * Hiding them from the type just stops us using these props by accident
 */
type MenuItemProps = Readonly<
    Omit<MuiMenuItemProps, 'dense' | 'disableGutters' | 'divider'> & {
        sizeClassName: string
    }
>

const checkIconStyle: SxProps<Theme> = { color: 'primary.700' }

/** `gap` isn't set here - `theme/dropdown.ts`'s own `MuiMenuItem.styleOverrides.root` already sets it (12px, matching Figma); an sx-level override here would win over that and silently drift from it */
const menuItemStyle: SxProps<Theme> = { display: 'flex', justifyContent: 'space-between' }
/**
 * `flexGrow` alone only stretches this box's own outer edge - without `display: 'flex'` too, a
 * percentage width set further inside it can't reliably resolve against that stretched size, leaving
 * content that wants to push something (e.g. trailing text) to the row's actual right edge stuck.
 */
const contentStyle: SxProps<Theme> = { display: 'flex', flexGrow: 1, minWidth: 0 }

/** Only rendered when selected - no reserved space for an absent checkmark on every other row */
export const MenuItem: FC<MenuItemProps> = ({ sizeClassName, className, children, selected, ...props }) => (
    <MuiMenuItem {...props} sx={menuItemStyle} selected={selected} className={[className, sizeClassName].filter(Boolean).join(' ')}>
        <Box sx={contentStyle}>{children}</Box>
        {selected && <CheckIcon sx={checkIconStyle} />}
    </MuiMenuItem>
)
